import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators
} from '@angular/forms';
import Swal from 'sweetalert2';
import { DeviceDetectorService } from '../../../../services/device-detector.service';
import { PointOfSaleService } from '../../../../services/pos';
import { HomeService } from '../../../../services/home.service';
import { MatSelect, MatSelectChange } from '@angular/material/select';
import { users } from '../../../../interfaces/users';

@Component({
  selector: 'app-create-pos',
  templateUrl: './create-pos.component.html',
  styleUrls: ['./create-pos.component.scss']
})
export class CreatePOSComponent implements OnInit {

  data!: FormGroup;
  providers = new FormControl();
  productname: users[] = [];
  filteredProviders: users[] = [];
  userSearchValue = '';

  selectedUsers: any[] = [];
  selectedFile: File | null = null;
  imagePreview: string | ArrayBuffer | null = null;

  contentReady = false;
  isUpdatePage = false;
  posId!: string;
  submitted = false;

  isMobile = false;
  isAr = false;

  userLastNamesFromUrl: string[] = [];

  warehouseTypes = [
    { id: 'company', value: 0, label: 'company' },
    { id: 'warehouse', value: 1, label: 'warehouse' },
    { id: 'store', value: 2, label: 'store' },
    { id: 'clinic', value: 3, label: 'clinic' },
    { id: 'storeandclinic', value: 4, label: 'store and clinic' },
    { id: 'shipping', value: 5, label: 'shipping' },
  ];

  @ViewChild('fileInput') fileInput!: ElementRef;
  @ViewChild('mySelect') mySelect!: MatSelect;

  constructor(
    private router: Router,
    private homeService: HomeService,
    private posService: PointOfSaleService,
    private fb: FormBuilder,
    private deviceService: DeviceDetectorService,
    private route: ActivatedRoute
  ) { }

  // ===============================
  // INIT
  // ===============================

  ngOnInit(): void {
    this.isMobile = this.deviceService.isMobileDevice();
    this.isAr = localStorage.getItem("language") === "ar";

    this.initForm();

    this.route.queryParams.subscribe(params => {
      if (params['id']) {
        this.isUpdatePage = true;
        this.posId = params['id'];

        this.data.patchValue({
          name: params['name'] || '',
          name_en: params['name_en'] || '',
          location: params['location'] || '',
          phonenumber: params['phonenumber'] || '',
          another_phonenumber: params['another_phonenumber'] || '',
          website_link: params['website_link'] || '',
          facebook_link: params['facebook_link'] || '',
          instagram_link: params['instagram_link'] || '',
          tiktok_link: params['tiktok_link'] || '',
          type: Number(params['warehouses_type']),
          edit_price: this.toBoolean(params['edit_price'], true),
          quantityguard: this.toBoolean(params['quantityguard'], true),
          has_delivery: this.toBoolean(params['has_delivery'], false)
        });

        this.loadBusinessSettings();

        if (params['image_url']) {
          this.imagePreview = params['image_url'];
        }

        const names = this.route.snapshot.queryParamMap.getAll('user_last_names');
        this.userLastNamesFromUrl = names.map(decodeURIComponent);
      }

      this.loadUsers();
    });
  }

  // ===============================
  // FORM
  // ===============================

  getUserName(id: number) {
    const user = this.productname.find((u: any) => u.id === id);
    return user ? this.getUserOptionLabel(user) : '';
  }

  getUserDisplayName(user: any): string {
    const firstName = String(user?.first_name || '').trim();

    return firstName || String(user?.username || '').trim();
  }

  getUserWarehouseName(user: any): string {
    const warehouseFields = [
      user?.warehouse?.name,
      user?.warehouse?.name_ar,
      user?.pos?.name,
      user?.pos?.name_ar,
      user?.branch?.name,
      user?.branch?.name_ar,
      user?.store?.name,
      user?.store?.name_ar,
      user?.warehouse_name,
      user?.warehouse_name_ar,
      user?.warehouses_name,
      user?.pos_name,
      user?.branch_name,
      user?.store_name,
    ];

    const directWarehouse = warehouseFields.find((value) => String(value || '').trim());
    if (directWarehouse) {
      return String(directWarehouse).trim();
    }

    const warehouses = user?.warehouses || user?.warehouse_users || user?.user_warehouses;
    if (Array.isArray(warehouses)) {
      const warehouseNames = warehouses
        .map((warehouse: any) => warehouse?.name || warehouse?.name_ar || warehouse?.warehouse?.name || warehouse?.warehouse?.name_ar)
        .filter((name: any) => String(name || '').trim())
        .join(', ');

      if (warehouseNames) {
        return warehouseNames;
      }
    }

    const firstName = String(user?.first_name || '').trim().replace(/\s+/g, ' ').toLowerCase();
    const lastName = String(user?.last_name || '').trim();
    const normalizedLastName = lastName.replace(/\s+/g, ' ').toLowerCase();

    if (lastName && normalizedLastName !== firstName) {
      return lastName;
    }

    return '';
  }

  getUserOptionLabel(user: any): string {
    const displayName = this.getUserDisplayName(user);
    const warehouseName = this.getUserWarehouseName(user);

    return warehouseName ? `${displayName} (${warehouseName})` : displayName;
  }

  getUserMeta(user: any): string {
    const values = [
      user?.username,
      user?.phone,
      user?.phonenumber,
    ]
      .map((value) => String(value || '').trim())
      .filter(Boolean);

    return Array.from(new Set(values)).join(' - ');
  }

  initForm(): void {
    this.data = this.fb.group({
      name: ['', Validators.required],
      name_en: ['', Validators.required],

      location: [''],

      phonenumber: ['', Validators.required],
      another_phonenumber: [''],

      website_link: [''],
      facebook_link: [''],
      instagram_link: [''],
      tiktok_link: [''],

      edit_price: [true],
      quantityguard: [true],
      has_delivery: [false],

      type: ['', Validators.required]
    });
  }

  private toBoolean(value: any, fallback: boolean): boolean {
    if (value === null || value === undefined || value === '') {
      return fallback;
    }

    return value === true || value === 'true' || value === 1 || value === '1';
  }

  private loadBusinessSettings(): void {
    if (!this.posId) return;

    this.posService.getBusinessSettings(this.posId).subscribe({
      next: (res) => {
        const settings = res?.settings ?? res?.business_setting ?? res;

        this.data.patchValue({
          edit_price: this.toBoolean(settings?.edit_price, true),
          quantityguard: this.toBoolean(settings?.quantityguard, true),
          has_delivery: this.toBoolean(settings?.has_delivery, false)
        });
      },
      error: () => {
        this.data.patchValue({
          edit_price: true,
          quantityguard: true,
          has_delivery: false
        });
      }
    });
  }

  // ===============================
  // FILE
  // ===============================

  onFileSelected(event: any): void {
    const file = event.target.files[0];
    if (!file) return;

    const maxSize = 2 * 1024 * 1024;

    if (file.size > maxSize) {
      Swal.fire({
        icon: 'error',
        title: this.isAr ? 'حجم الصورة كبير' : 'Image too large',
        text: this.isAr
          ? 'حجم الصورة يجب ألا يزيد عن 2 ميجابايت'
          : 'Image must be smaller than 2MB',
        confirmButtonText: this.isAr ? 'تمام' : 'OK'
      });

      event.target.value = '';
      this.selectedFile = null;
      this.imagePreview = null;
      return;
    }

    this.selectedFile = file;

    const reader = new FileReader();
    reader.onload = () => {
      this.imagePreview = reader.result;
    };
    reader.readAsDataURL(file);
  }

  // ===============================
  // USERS
  // ===============================

  loadUsers(): void {
    this.contentReady = true;

    this.homeService.getAllUsers().subscribe({
      next: (data: any[]) => {
        const usersWithoutClients = data.filter(
          user => String(user.type) !== '9'
        );

        this.productname = usersWithoutClients.sort((a, b) =>
          a.last_name.localeCompare(b.last_name, 'ar', { sensitivity: 'base' })
        );

        this.filteredProviders = [...this.productname];

        if (this.isUpdatePage) {
          const selectedIds = this.productname
            .filter(user =>
              this.userLastNamesFromUrl.includes(user.last_name)
            )
            .map(user => user.id);

          this.providers.setValue(selectedIds);
          this.selectedUsers = selectedIds;
        }

        this.contentReady = false;
      },
      error: () => {
        this.contentReady = false;
      }
    });
  }

  onProductSelected(event: MatSelectChange): void {
    this.selectedUsers = event.value || [];
  }

  onUserSearchChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.userSearchValue = input.value;
    const search = this.userSearchValue.trim().toLowerCase();

    if (!search) {
      this.filteredProviders = [...this.productname];
      return;
    }

    this.filteredProviders = this.productname.filter((user: any) => {
      const fullName = `${user.first_name || ''} ${user.last_name || ''}`.toLowerCase();
      const reverseName = `${user.last_name || ''} ${user.first_name || ''}`.toLowerCase();
      const username = String(user.username || '').toLowerCase();
      const phone = String(user.phone || user.phonenumber || '').toLowerCase();
      const warehouseName = this.getUserWarehouseName(user).toLowerCase();

      return fullName.includes(search) ||
        reverseName.includes(search) ||
        username.includes(search) ||
        phone.includes(search) ||
        warehouseName.includes(search);
    });
  }

  clearUserSearch(event?: Event): void {
    event?.stopPropagation();
    this.userSearchValue = '';
    this.filteredProviders = [...this.productname];
  }

  shouldShowError(controlName: string): boolean {
    const control = this.data?.get(controlName);
    return !!control && control.invalid && (control.touched || this.submitted);
  }

  shouldShowUsersError(): boolean {
    return this.submitted && !this.selectedUsers.length;
  }

  getErrorMessage(controlName: string, label: string): string {
    const control = this.data?.get(controlName);

    if (!control || !control.errors) {
      return '';
    }

    if (control.errors['required']) {
      return this.isAr ? `${label} مطلوب` : `${label} is required`;
    }

    return this.isAr ? `${label} غير صحيح` : `${label} is invalid`;
  }

  getMissingRequiredCount(): number {
    if (!this.data) {
      return 0;
    }

    return Object.keys(this.data.controls).filter((controlName) => {
      const control = this.data.get(controlName);
      return control?.hasError('required');
    }).length + (!this.selectedUsers.length ? 1 : 0);
  }

  private validateBeforeSubmit(): boolean {
    this.submitted = true;

    if (this.data.invalid || !this.selectedUsers.length) {
      this.data.markAllAsTouched();

      Swal.fire({
        icon: 'warning',
        title: this.isAr ? 'تأكد من بيانات البزنس' : 'Please check business data',
        text: this.isAr ? 'راجع الرسائل تحت الحقول المطلوبة أو غير الصحيحة' : 'Review the messages below the required or invalid fields',
        confirmButtonText: this.isAr ? 'تمام' : 'OK'
      });

      return false;
    }

    return true;
  }

  // ===============================
  // SUBMIT
  // ===============================

  onSubmit(): void {
    if (!this.validateBeforeSubmit()) {
      return;
    }

    if (!this.selectedUsers.length) {
      Swal.fire({
        icon: 'error',
        title: this.isAr
          ? "يرجى اختيار مستخدم واحد على الأقل"
          : 'Please select at least one user',
      });
      return;
    }

    this.contentReady = true;

    const formData = this.buildFormData();

    this.isUpdatePage
      ? this.updatePOS(formData)
      : this.createPOS(formData);
  }

  buildFormData(): FormData {
    const formData = new FormData();
    const values = this.data.value;

    formData.append('name', values.name);
    formData.append('name_en', values.name_en);

    formData.append('location', values.location || '');

    formData.append('phonenumber', values.phonenumber);
    formData.append('another_phonenumber', values.another_phonenumber || '');

    formData.append('website_link', values.website_link || '');
    formData.append('facebook_link', values.facebook_link || '');
    formData.append('instagram_link', values.instagram_link || '');
    formData.append('tiktok_link', values.tiktok_link || '');

    formData.append('warehouses_type', values.type);
    formData.append('edit_price', values.edit_price ? '1' : '0');
    formData.append('quantityguard', values.quantityguard ? '1' : '0');
    formData.append('has_delivery', values.has_delivery ? '1' : '0');

    this.selectedUsers.forEach(id => {
      formData.append('user_ids[]', id.toString());
    });

    if (this.selectedFile) {
      formData.append('image', this.selectedFile);
    }

    return formData;
  }

  // ===============================
  // API CALLS
  // ===============================

  createPOS(formData: FormData): void {
    this.posService.createPOS(formData).subscribe({
      next: (res) => {
        const warehouseId = res?.id || res?.warehouse?.id || res?.data?.id;
        this.saveBusinessSettings(warehouseId);

        Swal.fire({
          icon: 'success',
          title: this.isAr
            ? "تم اضافة بزمس"
            : 'business created',
          timer: 1500,
          showConfirmButton: false
        });

        this.isMobile
          ? this.router.navigateByUrl('/Pospage.mobile')
          : this.router.navigateByUrl('/Pospage');
      },
      error: () => {
        this.contentReady = false;
        Swal.fire({
          icon: 'error',
          title: this.isAr
            ? "فشل حفظ بزمس"
            : 'business not created'
        });
      }
    });
  }

  updatePOS(formData: FormData): void {
    this.posService.editPOS(formData, this.posId).subscribe({
      next: () => {
        this.saveBusinessSettings(this.posId);

        Swal.fire({
          icon: 'success',
          title: this.isAr
            ? "تم تعديل بزنس"
            : 'business updated',
          timer: 1500,
          showConfirmButton: false
        });

        this.isMobile
          ? this.router.navigateByUrl('/Pospage.mobile')
          : this.router.navigateByUrl('/Pospage');
      },
      error: () => {
        this.contentReady = false;
        Swal.fire({
          icon: 'error',
          title: this.isAr
            ? "فشل تعديل بزنس"
            : 'business not updated'
        });
      }
    });
  }

  private saveBusinessSettings(warehouseId: string | number | null | undefined): void {
    if (!warehouseId) return;

    this.posService.updateBusinessSettings(warehouseId, {
      edit_price: !!this.data.value.edit_price,
      quantityguard: !!this.data.value.quantityguard,
      has_delivery: !!this.data.value.has_delivery
    }).subscribe({
      error: (error) => console.error('Failed to save business settings:', error),
    });
  }

}